import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DirectorySearchResult, ExternalInvite, GrantableRole, Member } from '@mmf/contracts';
import {
  externalSignInUrl,
  fetchExternalInvites,
  fetchMembers,
  grantRole,
  inviteExternalPartner,
  inviteMember,
  removeMember,
  restoreMember,
  revokeRole,
  searchDirectory,
  suspendMember,
} from '../api/client';

const GRANTABLE_ROLES: GrantableRole[] = ['Moderator', 'Administrator'];

/**
 * Moderator or Administrator. Search the directory, invite someone (creates
 * them as Invited — see UserRepository.invite), then manage everyone
 * already here: suspend/restore, plus remove and role grant/revoke for an
 * Administrator. Removing a member, and suspending/removing themselves, an
 * administrator, or another moderator, are all higher-stakes than the rest
 * of this page and stay Administrator-only (for the latter two: a Moderator
 * acting on themselves or a peer/superior specifically — see
 * MembersService.guardModeratorCannotTargetSelfOrPeer); `isAdministrator`
 * and `currentUserId` just decide what's shown here — the API itself is the
 * actual boundary (MembersController's per-handler @Roles, and that guard).
 */
export function MemberAdmin({
  isAdministrator,
  currentUserId,
}: {
  isAdministrator: boolean;
  currentUserId: string;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const [query, setQuery] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [tooShort, setTooShort] = useState(false);
  const [memberFilter, setMemberFilter] = useState('');

  const members = useQuery({ queryKey: ['admin', 'members'], queryFn: fetchMembers });
  const invites = useQuery({ queryKey: ['admin', 'external-invites'], queryFn: fetchExternalInvites });

  const search = useQuery({
    queryKey: ['admin', 'directory', query],
    queryFn: () => searchDirectory(query),
    enabled: false,
  });

  const invite = useMutation({
    mutationFn: (result: DirectorySearchResult) =>
      inviteMember({
        entraObjectId: result.entraObjectId,
        displayName: result.displayName,
        department: result.department,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'members'] });
      await search.refetch();
    },
  });

  const handleSearch = (event: React.FormEvent): void => {
    event.preventDefault();
    if (query.trim().length < 3) {
      setTooShort(true);
      return;
    }
    setTooShort(false);
    setHasSearched(true);
    void search.refetch();
  };

  const pendingInvites = invites.data?.filter((entry) => entry.status === 'Pending') ?? [];
  const memberEmails = new Set(
    members.data?.flatMap((member) => (member.email ? [member.email] : [])) ?? [],
  );
  // Once a pending invite's email is already represented by a member row —
  // a re-invite of someone who has signed in before, most notably a Removed
  // partner being reactivated — that "awaiting sign-up" state shows on the
  // row itself (see MemberRow) instead of as a second, seemingly-unrelated
  // card here.
  const standaloneInvites = pendingInvites.filter((entry) => !memberEmails.has(entry.email));

  const filteredMembers = filterMembers(members.data ?? [], memberFilter);

  return (
    <section aria-labelledby="member-admin-heading" data-testid="member-admin">
      <h3 id="member-admin-heading">Members</h3>

      <h4 id="staff-invite-heading">Invite MortgageMAX staff</h4>
      <p className="hint">
        Searches your company directory (Microsoft Entra) for existing employees — not for
        business partners, who have no directory entry until they sign in themselves. See
        "Invite a business partner" below for that.
      </p>

      <form onSubmit={handleSearch} className="member-search" aria-labelledby="staff-invite-heading">
        <label htmlFor="member-search-query">Find a staff member to invite</label>
        <input
          id="member-search-query"
          value={query}
          placeholder="Name or email"
          onChange={(event) => setQuery(event.target.value)}
        />
        <button type="submit" className="button" data-testid="search-directory">
          Search
        </button>
      </form>

      {tooShort && (
        <p role="alert" data-testid="directory-search-too-short">
          Enter at least three characters to search.
        </p>
      )}

      {search.isFetching && <p role="status">Searching…</p>}

      {search.error && (
        <p role="alert" data-testid="directory-error">
          Directory search failed. Try again.
        </p>
      )}

      {hasSearched && !search.isFetching && search.data && search.data.length === 0 && (
        <p className="hint">No matches.</p>
      )}

      {search.data && search.data.length > 0 && (
        <ul className="directory-results">
          {search.data.map((result) => (
            <li key={result.entraObjectId} className="directory-result-row">
              <div>
                <strong>{result.displayName}</strong>
                <p className="hint">
                  {result.mail ?? 'no email on file'}
                  {result.department ? ` · ${result.department}` : ''}
                </p>
              </div>
              <button
                type="button"
                className="button small"
                disabled={result.isExistingMember || invite.isPending}
                onClick={() => invite.mutate(result)}
                data-testid={`invite-${result.entraObjectId}`}
              >
                {result.isExistingMember ? 'Already a member' : 'Invite'}
              </button>
            </li>
          ))}
        </ul>
      )}

      {invite.error && (
        <p role="alert" className="notice warning" data-testid="invite-error">
          Could not invite that person. Try again.
        </p>
      )}

      <hr />

      <ExternalPartnerInvite invites={invites} pendingInvites={standaloneInvites} />

      <hr />

      {members.isLoading && <p role="status">Loading members…</p>}

      {members.error && (
        <p role="alert" data-testid="members-error">
          Could not load the member list.
        </p>
      )}

      {members.data && members.data.length > 0 && (
        <div className="member-search">
          <label htmlFor="member-filter-input">Search members</label>
          <input
            id="member-filter-input"
            value={memberFilter}
            placeholder="Name or email"
            onChange={(event) => setMemberFilter(event.target.value)}
            data-testid="member-filter-input"
          />
        </div>
      )}

      {members.data && memberFilter.trim() !== '' && filteredMembers.length === 0 && (
        <p className="hint" data-testid="member-filter-no-matches">
          No members match &quot;{memberFilter}&quot;.
        </p>
      )}

      <ul className="member-list">
        {filteredMembers.map((member) => (
          <MemberRow
            key={member.id}
            member={member}
            isAdministrator={isAdministrator}
            currentUserId={currentUserId}
            pendingInvite={
              member.email ? pendingInvites.find((entry) => entry.email === member.email) : undefined
            }
          />
        ))}
      </ul>
    </section>
  );
}

/**
 * Case-insensitive substring match on name or email. Client-side over the
 * already-fetched list, not a new endpoint — this is an internal roster, not
 * a dataset large enough to need server-side search/pagination yet.
 */
function filterMembers(members: Member[], filterText: string): Member[] {
  const needle = filterText.trim().toLowerCase();
  if (needle === '') {
    return members;
  }
  return members.filter(
    (member) =>
      member.displayName.toLowerCase().includes(needle) ||
      (member.email?.toLowerCase().includes(needle) ?? false),
  );
}

/**
 * Member / Moderator / Administrator / "Moderator & Administrator" — the
 * two roles are independently grantable (see GRANTABLE_ROLES's checkboxes),
 * not mutually exclusive, so someone can hold both at once. Ordered to
 * match GRANTABLE_ROLES rather than however `member.roles` happens to come
 * back from the database.
 */
function roleLabel(member: Member): string {
  const specialRoles = GRANTABLE_ROLES.filter((role) => member.roles.includes(role));
  return specialRoles.length > 0 ? specialRoles.join(' & ') : 'Member';
}

/**
 * Business partners don't exist in any directory this tenant can see before
 * their first sign-in through Entra External ID, so — unlike the
 * search-and-invite box above — this is just an email address. This app
 * sends no email itself: the static sign-in link is shown after inviting so
 * whoever's inviting can copy it and share it with the partner however they
 * already would (Teams, their own email).
 */
function ExternalPartnerInvite({
  invites,
  pendingInvites,
}: {
  invites: { isLoading: boolean; error: Error | null };
  pendingInvites: ExternalInvite[];
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');

  const invite = useMutation({
    mutationFn: () => inviteExternalPartner({ email, displayName: displayName.trim() || undefined }),
    onSuccess: async () => {
      setEmail('');
      setDisplayName('');
      await queryClient.invalidateQueries({ queryKey: ['admin', 'external-invites'] });
      // A re-invite of someone who has already signed in before can update
      // that existing member row's status and displayName immediately — see
      // MembersService.inviteExternal.
      await queryClient.invalidateQueries({ queryKey: ['admin', 'members'] });
    },
  });

  return (
    <section aria-labelledby="external-invite-heading">
      <h4 id="external-invite-heading">Invite a business partner</h4>
      <p className="hint">
        No email is sent — share this link with the partner yourself once invited:{' '}
        <code>{externalSignInUrl('/')}</code>
      </p>

      <form
        className="member-search"
        onSubmit={(event) => {
          event.preventDefault();
          if (email.trim() === '') {
            return;
          }
          invite.mutate();
        }}
      >
        <label htmlFor="external-invite-email">Email address</label>
        <input
          id="external-invite-email"
          type="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <label htmlFor="external-invite-name">Display name (optional)</label>
        <input
          id="external-invite-name"
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
        />
        <button
          type="submit"
          className="button"
          disabled={invite.isPending || email.trim() === ''}
          data-testid="invite-external-submit"
        >
          Invite
        </button>
      </form>

      {invite.error && (
        <p role="alert" className="notice warning" data-testid="invite-external-error">
          Could not invite that email — check it hasn't already claimed an invite.
        </p>
      )}

      {invites.isLoading && <p role="status">Loading invites…</p>}

      {invites.error && (
        <p role="alert" data-testid="external-invites-error">
          Could not load pending invites.
        </p>
      )}

      {pendingInvites.length > 0 && (
        <ul className="member-list" data-testid="external-invite-list">
          {pendingInvites.map((entry: ExternalInvite) => (
            <li key={entry.id} className="member-row">
              <div>
                <strong>{entry.displayName ?? entry.email}</strong>
                <span className="badge"> Awaiting sign-up</span>
                {entry.displayName && <p className="hint">{entry.email}</p>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function MemberRow({
  member,
  isAdministrator,
  currentUserId,
  pendingInvite,
}: {
  member: Member;
  isAdministrator: boolean;
  currentUserId: string;
  pendingInvite?: ExternalInvite;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const [suspendReason, setSuspendReason] = useState('');
  const [isSuspending, setIsSuspending] = useState(false);

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['admin', 'members'] });
  };

  const suspend = useMutation({
    mutationFn: () => suspendMember(member.id, suspendReason),
    onSuccess: async () => {
      setIsSuspending(false);
      setSuspendReason('');
      await invalidate();
    },
  });
  const restore = useMutation({ mutationFn: () => restoreMember(member.id), onSuccess: invalidate });
  const remove = useMutation({ mutationFn: () => removeMember(member.id), onSuccess: invalidate });
  const toggleRole = useMutation({
    mutationFn: (role: GrantableRole) =>
      member.roles.includes(role) ? revokeRole(member.id, role) : grantRole(member.id, role),
    onSuccess: invalidate,
  });

  const anyActionError = suspend.error || restore.error || remove.error || toggleRole.error;

  // A Removed partner with a fresh Pending invite is on the way back in —
  // still showing REMOVED at that point reads as though re-inviting did
  // nothing, when it actually did: the invite is what MemberAdmin's parent
  // component used to decide this invite belongs on this row rather than in
  // the standalone "Invite a business partner" list above.
  const isAwaitingReinviteSignUp = member.status === 'Removed' && pendingInvite !== undefined;

  // Mirrors MembersService.guardSelfOrModeratorPeerAction — two separate
  // rules: nobody (Administrator included) may suspend or remove their own
  // account through this panel, and a Moderator additionally may not act on
  // anyone who holds the Moderator or Administrator role. The API is the
  // real boundary; this just avoids showing a button that would only ever
  // 403.
  const isSelf = member.id === currentUserId;
  const targetIsPeerOrSuperior = member.roles.includes('Moderator') || member.roles.includes('Administrator');
  const peerActionBlocked = !isAdministrator && targetIsPeerOrSuperior;

  return (
    <li className="member-row" data-testid={`member-${member.id}`}>
      <div>
        <strong>{member.displayName}</strong>
        <span className="badge" data-testid={`status-badge-${member.id}`}>
          {' '}
          {isAwaitingReinviteSignUp ? 'Awaiting sign-up' : member.status}
        </span>
        {/* Visible to every viewer, not just an Administrator — the
            checkboxes further down that actually grant/revoke a role stay
            Administrator-only, but seeing who already holds one is plain
            information a Moderator needs too (not least to understand why
            Suspend is unavailable on some rows — see moderatorBlockedFromSuspending). */}
        <span className="badge" data-testid={`role-badge-${member.id}`}>
          {' '}
          {roleLabel(member)}
        </span>
        {member.identitySource === 'External' && (
          <span className="badge" data-testid={`external-badge-${member.id}`}>
            {' '}
            External partner
          </span>
        )}
        <p className="hint" data-testid={`member-detail-${member.id}`}>
          {member.identitySource === 'External'
            ? // Links this row back to the same email the invite list above
              // shows for a pending invite — without it, a Removed partner's
              // row and a fresh "awaiting sign-up" invite for the same person
              // look like two unrelated things.
              (member.email ?? 'Business partner — no company directory entry')
            : (member.department ?? 'Not yet synced from the directory')}
        </p>
      </div>

      {anyActionError && (
        <p role="alert" className="notice warning">
          That action failed. Try again.
        </p>
      )}

      {isAdministrator &&
        (isSelf ? (
          // Same reasoning as the suspend/remove restriction above: no
          // legitimate reason to change your own roles through this panel,
          // and self-revoking Administrator especially is an easy accidental
          // click with no second set of eyes on it — see
          // MembersService.guardCannotChangeOwnRoles.
          <p className="hint" data-testid={`roles-restricted-${member.id}`}>
            You cannot change your own roles.
          </p>
        ) : (
          <div className="member-roles">
            {GRANTABLE_ROLES.map((role) => (
              <label key={role} className="checkbox">
                <input
                  type="checkbox"
                  checked={member.roles.includes(role)}
                  disabled={toggleRole.isPending}
                  onChange={() => toggleRole.mutate(role)}
                  data-testid={`role-${role}-${member.id}`}
                />
                {role}
              </label>
            ))}
          </div>
        ))}

      {member.status === 'Removed' ? (
        // Neither Suspend nor Remove make sense here — this member is
        // already gone, and re-inviting (not a button on this screen) is
        // the documented way back for both identity sources: the search-
        // and-invite box for staff, the business-partner box for partners.
        <p className="hint" data-testid={`removed-notice-${member.id}`}>
          {isAwaitingReinviteSignUp
            ? 'Invited again — waiting for them to sign in.'
            : 'Removed. Re-invite them (staff search above, or business-partner invite below) to restore access.'}
        </p>
      ) : (
        <div className="member-actions">
          {member.status === 'Suspended' ? (
            // Restoring your own account (someone else suspended you) is not
            // restricted — only Suspend/Remove are, so this stays untouched
            // by isSelf.
            <button
              type="button"
              className="button secondary small"
              disabled={restore.isPending}
              onClick={() => restore.mutate()}
              data-testid={`restore-${member.id}`}
            >
              Restore
            </button>
          ) : isSelf ? (
            <p className="hint" data-testid={`suspend-restricted-${member.id}`}>
              You cannot suspend or remove your own account.
            </p>
          ) : peerActionBlocked ? (
            <p className="hint" data-testid={`suspend-restricted-${member.id}`}>
              Moderators cannot suspend an administrator or another moderator.
            </p>
          ) : (
            <button
              type="button"
              className="button secondary small"
              onClick={() => setIsSuspending(true)}
              data-testid={`suspend-${member.id}`}
            >
              Suspend
            </button>
          )}

          {/* Same courtesy-only hiding the role checkboxes above use — the
              API's own @Roles('Administrator') on this endpoint is the real
              boundary. Removing a member outright is a harder-to-reverse
              action than suspend/restore, so it stays Administrator-only,
              same tier as granting/revoking a role — and, like suspend, is
              never available on your own row (the hint above already covers
              why, so no separate message here). */}
          {isAdministrator && !isSelf && (
            <button
              type="button"
              className="button secondary small"
              disabled={remove.isPending}
              onClick={() => remove.mutate()}
              data-testid={`remove-${member.id}`}
            >
              Remove
            </button>
          )}
        </div>
      )}

      {isSuspending && (
        <form
          className="suspend-form"
          onSubmit={(event) => {
            event.preventDefault();
            suspend.mutate();
          }}
        >
          <label htmlFor={`suspend-reason-${member.id}`}>Reason</label>
          <input
            id={`suspend-reason-${member.id}`}
            value={suspendReason}
            required
            onChange={(event) => setSuspendReason(event.target.value)}
          />
          <button
            type="submit"
            className="button small"
            disabled={suspend.isPending || suspendReason.trim() === ''}
            data-testid={`confirm-suspend-${member.id}`}
          >
            Confirm
          </button>
        </form>
      )}
    </li>
  );
}
